import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { DatePicker, Button, Select, Input, Table, message } from 'antd'
import { LogoutOutlined } from '@ant-design/icons'
import httpRequest from '../../services/http-request'
import { navigate, Link } from '@reach/router'
import {
  XYPlot,
  LineSeriesCanvas,
  XAxis,
  YAxis,
  Crosshair,
  DiscreteColorLegend,
  Highlight,
  VerticalRectSeries,
} from 'react-vis'

import LoadingSpinner from '../common/loading-spinner'

const { RangePicker } = DatePicker

const SpikesAnalyzer = (props) => {
  const BASE_URL = "https://analyser.lotusbook247.io";

  const [loading, setLoading] = useState(false)
  const [userName, setUserName] = useState('')

  const defaultDateRange = [moment().startOf('day'), moment().endOf('day')]

  const [dateRange, setDateRange] = useState(defaultDateRange)

  const [query, setQuery] = useState(
    `${BASE_URL}/market-odds?marketId=${
      props.marketId
    }&from=${dateRange[0].toISOString()}&to=${dateRange[1].toISOString()}`
  )

  const [data, setData] = useState([])

  /**
   * Date-range filter section
   */
  const dateFormat = 'Do MMM HH:mm:ss, YYYY'

  const handleDateRangeChange = (value) => {
    console.log(value)
    console.log(value.map((a) => a.toISOString()))
    const [from, to] = value
    setDateRange([from, to])
  }

  const getOddTypePrices = (data, runner, type) => {
    const runnerPrices = data.data.odds.map((o) => {
      return {
        x: o.lastUpdatedTime,
        y: o.runners.find((r) => r.selectionId === runner).ex[
          type === 'back' ? 'availableToBack' : 'availableToLay'
        ][0].price,
      }
    })

    return runnerPrices
  }

  const getChartData = (data) => {
    const chartData = []
    const runners = data.data.odds[0].runners.map((r) => r.selectionId)
    console.log(runners)

    for (let i = 0; i < runners.length; i++) {
      const r = runners[i]

      const back = getOddTypePrices(data, r, 'back')
      const lay = getOddTypePrices(data, r, 'lay')

      const max = Math.max(...[...back.map((b) => b.y), ...lay.map((l) => l.y)])

      const options = {
        bufferSize: 2,
        offset: max * 0.1,
        factor: 0.4,
      }

      const [backSpikes] = detectSpikes(back, options)
      const [laySpikes] = detectSpikes(lay, options)
      chartData.push({
        selectionId: r,
        back,
        backSpikes: backSpikes.map((s) => ({
          x0: s.start,
          x: s.end,
          y: max,
        })),
        lay,
        laySpikes: laySpikes.map((s) => ({
          x0: s.start,
          x: s.end,
          y: max,
        })),
      })
    }

    return chartData
  }

  useEffect(() => {
    const fetchMarketOdds = async () => {
      try {
        const res = await fetch(query)
        const data = await res.json()
        console.log(data, loading)

        if (data.data.odds.length == 0) {
          throw Error('No odds found.')
        }

        const chartData = getChartData(data)

        setData(chartData)
        setLoading(false)
      } catch (error) {
        console.log(error)
        setLoading(false)
        message.error(error.message, 5)
      }
    }
    setLoading(true)
    fetchMarketOdds()
  }, [query])

  useEffect(() => {
    const userRole = localStorage.getItem('role')
    const loggedUserName = localStorage.getItem('userName')

    if (!userRole || !loggedUserName) {
      props.navigate('/login')
    }

    setUserName(loggedUserName)
  }, [])

  const handleLogoutClick = () => {
    localStorage.removeItem('role')
    localStorage.removeItem('token')
    navigate('/login')
  }

  const handleApplyClick = () => {
    setQuery(
      `${BASE_URL}/market-odds?marketId=${
        props.marketId
      }&from=${dateRange[0].toISOString()}&to=${dateRange[1].toISOString()}`
    )
  }

  const handleChartRegionSelected = (area) => {
    if (!area) return
    console.log(area.left)

    const correction = 19800000 // 05:30 hours

    const from = new Date(Math.floor(area.left * 1000)).getTime() + correction
    const to = new Date(Math.floor(area.right * 1000)).getTime() + correction

    setSearchQuery(
      `/api/misc/getOrderList?marketId=${props.marketId}&from=${from}&to=${to}`
    )
  }

  const [searchQuery, setSearchQuery] = useState(null)
  const [searchResult, setSearchResult] = useState(null)

  useEffect(() => {
    const fetchMarketOrders = async () => {
      try {
        if(!searchQuery) return;
        
        const { data } = await httpRequest(searchQuery, 'GET')
        console.log(data)
        let selectionMap = {}
        if (
          data &&
          data.orders &&
          data.selections &&
          data.orders.length > 0 &&
          data.selections.length > 0
        ) {
          for (const s of data.selections) {
            selectionMap = {
              ...selectionMap,
              [s.id]: {
                data: data.orders.filter((o) => o.selectionId == s.id),
                name: s.name,
              },
            }
          }
        } else {
          throw Error('No bets found.')
        }

        console.log(selectionMap)

        setSearchResult(selectionMap)
      } catch (error) {
        message.error(error.message, 5)
      }
    }

    fetchMarketOrders()
  }, [searchQuery])

  const [crossHairValues, setCrossHairValues] = useState([])

  const _onNearestX = (value, { index }) => {
    const values = data.reduce((prev, curr) => {
      prev.push({
        selectionId: curr.selectionId,
        back: curr.back[index].y,
        lay: curr.lay[index].y,
        x: curr.back[index].x,
      })

      return prev
    }, [])
    setCrossHairValues(values)
  }

  function detectSpikes(
    data,
    options = {
      bufferSize: 2,
      offset: 0.3,
      factor: 0.8,
    }
  ) {
    let start = 0
    let indexes = []
    const { bufferSize } = options
    const buffer = []
    const z_scores = []

    data.forEach(({ x, y }, i) => {
      buffer.push(y)
      if (buffer.length > bufferSize) {
        buffer.shift()
      }
      const m = mean(buffer)
      const score = (y - m) / standardDeviation(buffer) || 0
      z_scores.push(score)

      if (z_scores[i - 1] && score > z_scores[i - 1]) {
        start = x
      }

      if (
        z_scores[i - 1] &&
        score < 0 &&
        score < z_scores[i - 1] &&
        buffer[buffer.length - 2] > options.offset + options.factor * y
      ) {
        indexes.push({ start, end: x })
      }
    })

    return [indexes, z_scores]
  }

  function standardDeviation(values) {
    var avg = mean(values)

    var squareDiffs = values.map(function (value) {
      var diff = value - avg
      var sqrDiff = diff * diff
      return sqrDiff
    })

    var avgSquareDiff = mean(squareDiffs)

    var stdDev = Math.sqrt(avgSquareDiff)
    return stdDev
  }

  function mean(data) {
    var sum = data.reduce(function (sum, value) {
      return sum + value
    }, 0)

    var avg = sum / data.length
    return avg
  }

  const _onMouseLeave = () => {
    setCrossHairValues([])
  }

  const tickFormat = (v) => moment(v * 1000).format('MM-DD hh:mm:ss')

  return (
    <>
      <div className="page-container">
        <div className="header">
          <div className="header__title">
            <h2>Spikes Analyzer</h2>

            <span className="header-span--pull-right">
              <span className="user-name">{userName}</span>
              <Button onClick={handleLogoutClick} type="primary" size="small">
                Logout <LogoutOutlined />
              </Button>
            </span>
          </div>

          <div className="header__filters">
            <Link to="/pl-report-by-market">Markets</Link> &nbsp; &gt;
            {` ${props.marketId}`}
            <span style={{ marginLeft: 20 }}>
              <RangePicker
                defaultValue={dateRange}
                // format={dateFormat}
                // showTime={{ format: 'HH:mm' }}
                size={'default'}
                onChange={handleDateRangeChange}
              />
            </span>
            <span style={{ marginLeft: 20 }}>
              <Button type="primary" onClick={handleApplyClick}>
                Apply
              </Button>
            </span>
          </div>
          {loading && <LoadingSpinner />}
          <div style={{ display: 'flex' }}>
            <div class="graph">
              {data &&
                data.length > 0 &&
                data.map((d) => (
                  <>
                    <div style={{ display: 'flex', marginBottom: 10 }}>
                      <div>
                        <div style={{ display: 'flex', fontWeight: 'bold ' }}>
                          {d.selectionId}
                        </div>
                        <XYPlot
                          height={300}
                          width={1200}
                          key={d.selectionId}
                          onMouseLeave={_onMouseLeave}
                        >
                          {d.back && d.back.length > 0 && (
                            <LineSeriesCanvas
                              color="#f00"
                              opacity={0.7}
                              data={d.back}
                              onNearestX={_onNearestX}
                            />
                          )}
                          {d.lay && d.lay.length > 0 && (
                            <LineSeriesCanvas
                              color="#00f"
                              opacity={0.7}
                              data={d.lay}
                            />
                          )}

                          <XAxis
                            tickTotal={10}
                            tickFormat={tickFormat}
                            tickLabelAngle={-0}
                          />
                          <YAxis />

                          {d.backSpikes && d.backSpikes.length > 0 && (
                            <VerticalRectSeries
                              data={d.backSpikes}
                              style={{
                                fill: '#ff0000',
                                stroke: '#ff000022',
                                strokeOpacity: '0',
                                fillOpacity: '0.4',
                              }}
                            />
                          )}

                          {d.laySpikes && d.laySpikes.length > 0 && (
                            <VerticalRectSeries
                              data={d.laySpikes}
                              style={{
                                fill: '#0000ff',
                                stroke: '#0000ff22',
                                strokeOpacity: '0',
                                fillOpacity: '0.4',
                              }}
                            />
                          )}
                          <Highlight
                            drag
                            enableY={false}
                            onBrushEnd={handleChartRegionSelected}
                            //   onDragEnd={(area) => console.log(area)}
                          />
                          <Crosshair values={crossHairValues}>
                            <div
                              style={{
                                width: 100,
                                padding: 5,
                                borderRadius: 3,
                                background: '#00000080',
                                display: "flex",
                                flexDirection: "column"
                              }}
                            >
                              {crossHairValues.map((v, i) => (
                                <div style={{display:"flex"}}>
                                  <span style={{ fontWeight: 'bold', width:150 }}>
                                    {v.selectionId}
                                  </span>{' '}
                                  <span
                                    style={{
                                      background: '#ff000055',
                                      padding: 2,
                                      borderRadius: 2,
                                      width: 40
                                    }}
                                  >
                                    {v.back}
                                  </span>{' '}
                                  <span
                                    style={{
                                      background: '#0000ff55',
                                      padding: 2,
                                      borderRadius: 2,
                                      width: 40
                                    }}
                                  >
                                    {v.lay}
                                  </span>
                                </div>
                              ))}
                              {crossHairValues.length > 0 && (
                                <span>
                                  {moment(crossHairValues[0].x * 1000).format(
                                    'YYYY-MM-DD hh:mm:ss A'
                                  )}
                                </span>
                              )}
                            </div>
                          </Crosshair>
                        </XYPlot>
                      </div>
                      <div className="bets-tables">
                        {searchResult && searchResult[d.selectionId] && (
                          <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <div
                              style={{
                                padding: 5,
                                display: 'flex',
                                fontWeight: 'bold',
                              }}
                            >
                              {searchResult[d.selectionId].name} (
                              {d.selectionId})
                            </div>
                            <span></span>
                            <Table
                              size="small"
                              pagination={false}
                              scroll={{ x: 98, y: 300 }}
                              rowClassName={(r, i) => {
                                if (r.backLayFlag == 'L') {
                                  return 'bet-lay'
                                }
                                if (r.backLayFlag == 'B') {
                                  return 'bet-back'
                                }
                              }}
                              columns={[
                                {
                                  title: 'Bet ID',
                                  dataIndex: 'betId',
                                },
                                {
                                  title: 'IP',
                                  dataIndex: 'IP',
                                },
                                {
                                  title: 'Login Name',
                                  dataIndex: 'loginName',
                                },
                                {
                                  title: 'Member Code',
                                  dataIndex: 'memberCode',
                                  width: 100,
                                },
                                {
                                  title: 'Date',
                                  dataIndex: 'betDate',
                                  width: 100,
                                },
                                {
                                  title: 'oddsRequested',
                                  dataIndex: 'oddsRequested',
                                },
                                {
                                  title: 'oddsMatched',
                                  dataIndex: 'oddsMatched',
                                },
                                {
                                  title: 'stakeRequested',
                                  dataIndex: 'stakeRequested',
                                },
                                {
                                  title: 'stakeMatched',
                                  dataIndex: 'stakeMatched',
                                },
                              ]}
                              dataSource={searchResult[d.selectionId].data}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ))}
              {data.length > 0 && (
                <DiscreteColorLegend
                  height={100}
                  width={200}
                  items={['back', 'lay']}
                  orientation="horizontal"
                />
              )}
            </div>
            <div
              style={{
                textAlign: 'left',
                padding: 10,
                borderRadius: 5,
                height: '100vh',
                overflow: 'auto',
              }}
            >
              {/* {searchResult && (
                <ReactJson
                  // collapsed={true}
                  style={{ fontSize: 10, padding: 10 }}
                  theme="monokai"
                  src={searchResult}
                />
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SpikesAnalyzer
